import { checkSession } from "@features/auth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Avatar, AvatarFallback } from "@shadcn/ui/avatar";
import { Button } from "@shadcn/ui/button";
import { Checkbox } from "@shadcn/ui/checkbox";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@shadcn/ui/drawer";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { Building2, Check, KeySquare, Mail, Trash2, X } from "lucide-react";
import { useState } from "react";
import { type PreloadedQuery, usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { getInitials, type LockPermission, ShareAccessDrawer } from "~/components/lock/MyLocks/ShareAccessDrawer";
import { ShareAccessAccountDrawer } from "~/components/lock/MyLocks/ShareAccessAccountDrawer";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { toast } from "sonner";
import type { sharedAccessPageQuery } from "./__generated__/sharedAccessPageQuery.graphql";
import type { sharedAccessPageRevokeMutation } from "./__generated__/sharedAccessPageRevokeMutation.graphql";
import type { sharedAccessPageUpdateMutation } from "./__generated__/sharedAccessPageUpdateMutation.graphql";

export const query = graphql`
	query sharedAccessPageQuery($hubName: String!, $reservationId: UUID!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			street
			city
			postalCode
			hub @required(action: THROW) {
				id
			}
		}
		reservation(id: $reservationId) {
			... on LockReservation {
				id
				subtype {
					__typename
				}
				space {
					id
					frame
					position
				}
				utilizationGrants(filter: { active: true }, pagination: { page: 0, pageSize: 1000 }) @catch(to: NULL) {
					nodes {
						id
						ty
						permissions
						toUser {
							id
							name
							email
						}
						toOrganization {
							id
							name
						}
						toEmail
					}
				}
			}
		}
	}
`;

const revokeMutation = graphql`
	mutation sharedAccessPageRevokeMutation($id: UUID!) {
		lockUtilizationGrantDeactivate(id: $id) {
			id
			deactivatedAt
		}
	}
`;

const updateMutation = graphql`
	mutation sharedAccessPageUpdateMutation($input: UtilizationGrantUpdateInput!) {
		lockUtilizationGrantUpdate(input: $input) {
			id
			permissions
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/swap/shared-access/$reservationId")({
	beforeLoad: () => {
		document.title = "bacehub | Lock";
	},
	component: RouteComponent,
	loader: async (ctx) => {
		const sessionExists = await checkSession();
		if (!sessionExists) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/swap/my${ctx.location.searchStr}` },
			});
		}
		return preloadQuery<sharedAccessPageQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
			reservationId: ctx.params.reservationId,
		});
	},
});

type Grant = NonNullable<NonNullable<NonNullable<sharedAccessPageQuery["response"]["reservation"]>["utilizationGrants"]>>["nodes"][number];

function RouteComponent() {
	const { hubMetaName, reservationId } = Route.useParams();
	return <SharedAccessPage queryRef={Route.useLoaderData()} hubMetaName={hubMetaName} reservationId={reservationId} service="LOCK_B2B" />;
}

export function SharedAccessPage(props: {
	queryRef: PreloadedQuery<sharedAccessPageQuery>;
	hubMetaName: string;
	reservationId: string;
	service: "LOCK_B2B" | "LOCK_B2C";
}) {
	const i18n = useI18nContext().LL["new-ui"];
	const router = useRouter();
	const { hubMetaName, reservationId, service } = props;
	const { hubMeta, reservation } = usePreloadedQuery<sharedAccessPageQuery>(query, props.queryRef);

	const [revokeGrant, isRevoking] = useAsyncMutation<sharedAccessPageRevokeMutation>(revokeMutation);
	const [updateGrant, isUpdating] = useAsyncMutation<sharedAccessPageUpdateMutation>(updateMutation);
	const [isEmailSharingOpen, setIsEmailSharingOpen] = useState(false);
	const [isAccountSharingOpen, setIsAccountSharingOpen] = useState(false);
	const [isEmailShareConfirmed, setIsEmailShareConfirmed] = useState(false);
	const [revokeTarget, setRevokeTarget] = useState<Grant | null>(null);

	const grants = reservation?.utilizationGrants?.nodes ?? [];
	const space = reservation?.space;
	const isB2C = reservation?.subtype?.__typename === "B2ClockReservationSubtype";
	const backToLocks = service === "LOCK_B2C" ? "/$hubMetaName/lock" : "/$hubMetaName/swap/my";
	const address = `${hubMeta.street}, ${hubMeta.postalCode} ${hubMeta.city}`;

	const handleRevoke = async () => {
		if (!revokeTarget) return;
		try {
			await revokeGrant({ variables: { id: revokeTarget.id } });
			setRevokeTarget(null);
			await router.invalidate();
		} catch (error) {
			console.error(error);
		}
	};

	const handleUpdatePermissions = async (grantId: string, permissions: LockPermission[]) => {
		try {
			await updateGrant({ variables: { input: { id: grantId, permissions } } });
			toast.success(i18n.LOCK_SHARE_PERMISSIONS_UPDATED());
			await router.invalidate();
		} catch (error) {
			console.error(error);
			toast.error(i18n.LOCK_SHARE_PERMISSIONS_UPDATE_FAILED());
		}
	};

	return (
		<PageContainer
			footer={
				<div className="flex flex-col gap-3">
					{isEmailShareConfirmed ? (
						<div className="flex items-center gap-2 rounded-lg border bg-background p-4 shadow-xs">
							<Check className="size-5 shrink-0 text-foreground" />
							<span className="text-sm font-medium">
								<T>{i18n.LOCK_SHARED_ACCESSES_VIA_EMAIL()}</T>
							</span>
						</div>
					) : null}
					<Button size="lg" className="w-full shadow-xl" onClick={() => setIsEmailSharingOpen(true)}>
						<Mail className="size-4" />
						<T>{i18n.LOCK_SHARED_ACCESSES_SHARE_EMAIL_CTA()}</T>
					</Button>
					<Button size="lg" variant="outline" className="w-full shadow-xl" onClick={() => setIsAccountSharingOpen(true)}>
						<KeySquare className="size-4" />
						<T>{isB2C ? i18n.LOCK_SHARED_ACCESSES_SHARE_PERSON_CTA() : i18n.LOCK_SHARED_ACCESSES_SHARE_ACCOUNT_CTA()}</T>
					</Button>
					<Button size="lg" variant="ghost" className="w-full" asChild>
						<Link to={backToLocks} params={{ hubMetaName }}>
							<T>{i18n.LOCK_SHARED_ACCESSES_BACK()}</T>
						</Link>
					</Button>
				</div>
			}
		>
			<AppHeader service={service} />

			<div className="flex flex-col gap-6 px-6 pt-6">
				<h1 className="text-2xl font-breit font-medium break-words">
					<T>{i18n.LOCK_SHARED_ACCESSES_TITLE()}</T>
				</h1>

				{/* Compartment number + location */}
				<div className="flex flex-col">
					<div className="flex items-start justify-between gap-4 border-t py-3.5 first:border-t-0">
						<span className="text-sm font-breit font-medium text-foreground">
							<T>{i18n.LOCK_SHARED_ACCESSES_COMPARTMENT_NR()}</T>
						</span>
						<span className="text-right text-base text-muted-foreground">{space ? space.position : "-"}</span>
					</div>
					<div className="flex items-start justify-between gap-4 border-t py-4">
						<span className="text-sm font-breit font-medium text-foreground">
							<T>{i18n.LOCK_SHARED_ACCESSES_LOCATION()}</T>
						</span>
						<div className="flex max-w-[60%] flex-col text-right">
							<span className="text-base text-muted-foreground break-words">{hubMeta.name}</span>
							<span className="text-sm text-muted-foreground underline break-words">{address}</span>
						</div>
					</div>
				</div>

				{/* Shared with list */}
				<div className="flex flex-col gap-4">
					<p className="text-base font-breit font-medium">
						<T>{i18n.LOCK_SHARED_ACCESSES_SHARED_WITH()}</T>
					</p>
					{grants.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							<T>{i18n.LOCK_SHARED_ACCESSES_EMPTY()}</T>
						</p>
					) : (
						<ul className="flex flex-col">
							{grants.map((grant) => (
								<SharedAccessRow
									key={grant.id}
									grant={grant}
									isBusy={isRevoking || isUpdating}
									onUpdatePermissions={handleUpdatePermissions}
									onRevoke={setRevokeTarget}
								/>
							))}
						</ul>
					)}
				</div>
			</div>

			<ShareAccessDrawer
				open={isEmailSharingOpen}
				onOpenChange={setIsEmailSharingOpen}
				onShared={() => setIsEmailShareConfirmed(true)}
				reservationId={reservationId}
			/>
			<ShareAccessAccountDrawer
				open={isAccountSharingOpen}
				onOpenChange={setIsAccountSharingOpen}
				reservationId={reservationId}
				isB2C={isB2C}
			/>

			<RevokeAccessDrawer
				grant={revokeTarget}
				isBusy={isRevoking}
				onCancel={() => setRevokeTarget(null)}
				onConfirm={() => void handleRevoke()}
			/>
		</PageContainer>
	);
}

function SharedAccessRow(props: {
	grant: Grant;
	isBusy: boolean;
	onUpdatePermissions: (grantId: string, permissions: LockPermission[]) => Promise<void>;
	onRevoke: (grant: Grant) => void;
}) {
	const i18n = useI18nContext().LL["new-ui"];
	const { grant, isBusy, onUpdatePermissions, onRevoke } = props;

	const isOrganization = grant.ty === "ORGANIZATION";
	const isAnonymous = grant.ty === "ANONYMOUS";
	const name = isOrganization ? grant.toOrganization?.name : isAnonymous ? null : grant.toUser?.name;
	const email = isOrganization ? null : isAnonymous ? grant.toEmail : grant.toUser?.email;
	const permissions = (grant.permissions ?? []) as LockPermission[];
	const togglePermission = (permission: LockPermission) => {
		const next = permissions.includes(permission) ? permissions.filter((item) => item !== permission) : [...permissions, permission];
		if (next.length > 0) void onUpdatePermissions(grant.id, next);
	};

	return (
		<li className="flex flex-col gap-2 border-t py-3 first:border-t-0">
			<div className="flex items-center gap-3">
				<Avatar className="size-10 shrink-0">
					<AvatarFallback>
						{isOrganization ? <Building2 className="size-4" /> : isAnonymous ? <Mail className="size-4" /> : getInitials(name, email)}
					</AvatarFallback>
				</Avatar>
				<div className="flex min-w-0 flex-1 flex-col">
					<span className="truncate text-sm font-breit font-medium">{name || email || "-"}</span>
					{name && email ? <span className="truncate text-sm text-muted-foreground">{email}</span> : null}
				</div>
				<Button
					variant="destructive"
					size="icon"
					className="shrink-0"
					disabled={isBusy}
					aria-label={i18n.LOCK_SHARE_REMOVE()}
					onClick={() => onRevoke(grant)}
				>
					<Trash2 className="size-4" />
				</Button>
			</div>
			<div className="flex flex-col gap-1.5 pl-[3.25rem]">
				<label className="flex cursor-pointer items-center gap-2">
					<Checkbox checked={permissions.includes("UNLOCK")} disabled={isBusy} onCheckedChange={() => togglePermission("UNLOCK")} />
					<span className="text-sm">
						<T>{i18n.LOCK_SHARE_PERMISSION_UNLOCK()}</T>
					</span>
				</label>
				<label className="flex cursor-pointer items-center gap-2">
					<Checkbox checked={permissions.includes("END_RENTAL")} disabled={isBusy} onCheckedChange={() => togglePermission("END_RENTAL")} />
					<span className="text-sm">
						<T>{i18n.LOCK_SHARE_PERMISSION_END_RENTAL()}</T>
					</span>
				</label>
			</div>
		</li>
	);
}

function RevokeAccessDrawer(props: { grant: Grant | null; isBusy: boolean; onCancel: () => void; onConfirm: () => void }) {
	const i18n = useI18nContext().LL["new-ui"];
	const { grant, isBusy, onCancel, onConfirm } = props;

	const name = grant?.ty === "ORGANIZATION" ? grant.toOrganization?.name : grant?.ty === "ANONYMOUS" ? null : grant?.toUser?.name;
	const email = grant?.ty === "ORGANIZATION" ? null : grant?.ty === "ANONYMOUS" ? grant.toEmail : grant?.toUser?.email;

	return (
		<Drawer open={grant !== null} onOpenChange={(open) => !open && onCancel()}>
			<DrawerContent>
				<DrawerHeader className="flex-row items-start justify-between gap-4 text-left">
					<div className="flex flex-col gap-1">
						<DrawerTitle className="text-2xl font-breit font-medium">
							<T>{i18n.LOCK_SHARE_REVOKE_CONFIRM_TITLE()}</T>
						</DrawerTitle>
						<DrawerDescription className="text-sm text-muted-foreground">
							<T>{i18n.LOCK_SHARE_REVOKE_CONFIRM_DESC()}</T>
						</DrawerDescription>
					</div>
					<DrawerClose asChild>
						<Button variant="ghost" size="icon" className="shrink-0" disabled={isBusy}>
							<X className="size-5" />
						</Button>
					</DrawerClose>
				</DrawerHeader>

				{grant ? (
					<div className="flex items-center gap-3 border-t px-4 py-4">
						<Avatar className="size-12 shrink-0">
							<AvatarFallback>{getInitials(name, email)}</AvatarFallback>
						</Avatar>
						<div className="flex min-w-0 flex-col">
							<span className="truncate text-base font-medium">{name || email || "-"}</span>
							{name && email ? <span className="truncate text-sm text-muted-foreground">{email}</span> : null}
						</div>
					</div>
				) : null}

				<DrawerFooter className="border-t">
					<Button
						variant="destructive"
						className="w-full shadow-xs"
						disabled={isBusy}
						onClick={(e) => {
							e.preventDefault();
							onConfirm();
						}}
					>
						<T>{i18n.LOCK_SHARE_REVOKE_CONFIRM_CTA()}</T>
					</Button>
					<DrawerClose asChild>
						<Button variant="outline" className="w-full" disabled={isBusy}>
							<T>{i18n.LOCK_SHARE_REVOKE_CANCEL()}</T>
						</Button>
					</DrawerClose>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
}
